import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  listUsers,
  updateUserStatus,
  WholesaleUser,
  UserStats,
} from '../../services/adminApi';

/**
 * Admin Dashboard Page
 *
 * Shows pending approval queue and overview stats
 */
export default function AdminDashboard() {
  const [users, setUsers] = useState<WholesaleUser[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listUsers();
      setUsers(response.users);
      setStats(response.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async (
    userId: string,
    action: 'approve' | 'reject'
  ) => {
    try {
      setActionLoading(userId);
      await updateUserStatus(userId, action);
      // Refresh data after action
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  // Get pending users
  const pendingUsers = users.filter(
    (u) => u.approvalStatus === 'pending' || !u.approvalStatus
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">{error}</div>
        <button onClick={fetchData} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Users"
          value={stats?.total || 0}
          color="default"
        />
        <StatCard
          label="Pending"
          value={stats?.pending || 0}
          color="amber"
          highlight={stats?.pending ? stats.pending > 0 : false}
        />
        <StatCard
          label="Approved"
          value={stats?.approved || 0}
          color="green"
        />
        <StatCard
          label="Rejected"
          value={stats?.rejected || 0}
          color="red"
        />
      </div>

      {/* Pending Approvals */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-display text-cream tracking-wide">
            Pending Approvals
          </h2>
          <Link
            to="/admin/users"
            className="text-sm text-accent hover:text-accent/80 transition-colors"
          >
            View All Users &rarr;
          </Link>
        </div>

        {pendingUsers.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <svg
              className="w-12 h-12 mx-auto mb-4 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>No pending approvals</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] text-left">
                  <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                    User
                  </th>
                  <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                    Company
                  </th>
                  <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                    Tax ID
                  </th>
                  <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                    Registered
                  </th>
                  <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((user) => (
                  <motion.tr
                    key={user.userId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-white/[0.04] last:border-0 hover:bg-white/5"
                  >
                    <td className="py-4">
                      <div className="font-medium text-cream">{user.name}</div>
                      <div className="text-sm text-text-muted">{user.email}</div>
                    </td>
                    <td className="py-4 text-sm text-cream">
                      {user.companyName || <span className="text-text-muted">—</span>}
                    </td>
                    <td className="py-4 text-sm font-mono text-cream">
                      {user.taxId || <span className="text-text-muted">—</span>}
                    </td>
                    <td className="py-4 text-sm text-text-muted">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleAction(user.userId, 'approve')}
                          disabled={actionLoading === user.userId}
                          className="px-3 py-1 text-xs uppercase tracking-wide bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {actionLoading === user.userId ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleAction(user.userId, 'reject')}
                          disabled={actionLoading === user.userId}
                          className="px-3 py-1 text-xs uppercase tracking-wide bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {actionLoading === user.userId ? '...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Stat Card Component
 */
function StatCard({
  label,
  value,
  color,
  highlight = false,
}: {
  label: string;
  value: number;
  color: 'default' | 'amber' | 'green' | 'red';
  highlight?: boolean;
}) {
  const colorClasses = {
    default: 'bg-white/5 text-cream',
    amber: highlight ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-900/30 text-amber-300',
    green: 'bg-green-900/30 text-green-300',
    red: 'bg-red-900/30 text-red-300',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-lg ${colorClasses[color]} ${
        highlight ? 'ring-2 ring-amber-500/50' : ''
      }`}
    >
      <div className="text-3xl font-display">{value}</div>
      <div className="text-sm opacity-80 uppercase tracking-wide">{label}</div>
    </motion.div>
  );
}

/**
 * Format date string
 */
function formatDate(dateString: string): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
